"use client"

import { CaretDown, CaretUp } from "@phosphor-icons/react"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import Image from "next/image"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { useScreenerTableData } from "./hooks/useScreenerTableData"

const STATIC_URL = "https://static1.tokenterminal.com/"

interface ProjectLogoProps {
  logo: string
  name: string
  size?: number
}

function ProjectLogo({ logo, name, size = 20 }: ProjectLogoProps) {
  if (!logo) {
    return (
      <div
        className="rounded-full bg-bg-muted flex items-center justify-center shrink-0"
        style={{ width: size, height: size }}
      >
        <span className="font-medium text-fg-secondary" style={{ fontSize: size * 0.4 }}>
          {name.charAt(0).toUpperCase()}
        </span>
      </div>
    )
  }

  const logoUrl = new URL(logo, STATIC_URL).href

  return (
    <Image
      src={logoUrl}
      alt={name}
      width={size}
      height={size}
      className="rounded-full object-cover shrink-0"
    />
  )
}

interface ChainLogosProps {
  chainIds: string[]
  chainsById: Map<string, { chain_id: string; name: string; logo: string }>
  maxDisplay?: number
}

function ChainLogos({ chainIds, chainsById, maxDisplay = 3 }: ChainLogosProps) {
  if (!chainIds || chainIds.length === 0) return null

  const displayChains = chainIds.slice(0, maxDisplay)
  const remainingCount = chainIds.length - maxDisplay

  return (
    <div className="flex items-center">
      <div className="flex items-center -space-x-1">
        {displayChains.map((chainId, index) => {
          const chain = chainsById.get(chainId)
          if (!chain) return null

          const logoUrl = new URL(chain.logo, STATIC_URL).href

          return (
            <Tooltip key={chainId} content={chain.name}>
              <Image
                src={logoUrl}
                alt={chain.name}
                width={16}
                height={16}
                className="rounded-full object-cover border border-bg-surface"
                style={{ zIndex: displayChains.length - index }}
              />
            </Tooltip>
          )
        })}
      </div>
      {remainingCount > 0 && (
        <span className="ml-1 text-xs text-fg-tertiary">+{remainingCount}</span>
      )}
    </div>
  )
}

function formatCurrency(value: number | undefined): string {
  if (value === undefined || value === null) return "-"
  return formatNumber({
    value,
    metricFormat: "price",
    prefix: "$",
    decimals: 1,
    abbreviationFormat: "compact",
  })
}

function formatTrend(value: number | undefined): {
  text: string
  color: string
} {
  if (value === undefined || value === null) return { text: "-", color: "" }

  const percentage = value * 100
  const isPositive = percentage >= 0
  const text = `${isPositive ? "+" : ""}${percentage.toFixed(1)}%`
  const color = isPositive ? "text-fg-emerald" : "text-fg-red"

  return { text, color }
}

function TableSkeleton() {
  return (
    <div className="w-full">
      {/* Header skeleton */}
      <div className="flex gap-4 py-2 border-b border-border-default">
        <div className="h-8 w-[140px] bg-bg-muted rounded-sm animate-pulse" />
        <div className="h-8 w-[130px] bg-bg-muted rounded-sm animate-pulse" />
        <div className="h-8 w-[80px] bg-bg-muted rounded-sm animate-pulse" />
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="h-8 w-[85px] bg-bg-muted rounded-sm animate-pulse" />
        ))}
      </div>
      {/* Row skeletons */}
      {Array.from({ length: 12 }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 py-2 border-b border-border-default">
          <div className="flex items-center gap-2 w-[140px]">
            <div className="w-4 h-4 bg-bg-muted rounded-sm animate-pulse" />
            <div className="w-5 h-5 bg-bg-muted rounded-full animate-pulse" />
            <div className="h-4 w-16 bg-bg-muted rounded-sm animate-pulse" />
          </div>
          <div className="h-4 w-[130px] bg-bg-muted rounded-sm animate-pulse" />
          <div className="h-4 w-[80px] bg-bg-muted rounded-sm animate-pulse" />
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-4 w-[85px] bg-bg-muted rounded-sm animate-pulse" />
          ))}
        </div>
      ))}
    </div>
  )
}

// Sort indicator component
function SortIndicator() {
  return (
    <div className="flex flex-col items-center ml-1 opacity-40">
      <CaretUp weight="fill" className="w-2.5 h-2.5 -mb-0.5" />
      <CaretDown weight="fill" className="w-2.5 h-2.5 -mt-0.5" />
    </div>
  )
}

// Header cell component with two-line format and sort indicator
function HeaderCell({
  label,
  subLabel,
  align = "right",
  width,
}: {
  label: string
  subLabel?: string
  align?: "left" | "right"
  width: string
}) {
  return (
    <th
      className={`py-2 px-3 font-normal whitespace-nowrap ${
        align === "left" ? "text-left" : "text-right"
      }`}
      style={{ width, minWidth: width }}
    >
      <div
        className={`flex items-center gap-0.5 text-xs ${
          align === "left" ? "justify-start" : "justify-end"
        }`}
      >
        <div className="flex flex-col items-end">
          <span className="text-fg-secondary">{label}</span>
          {subLabel && <span className="text-fg-quaternary text-[10px]">{subLabel}</span>}
        </div>
        <SortIndicator />
      </div>
    </th>
  )
}

interface ScreenerTablePreviewProps {
  className?: string
}

export function ScreenerTablePreview({ className = "" }: ScreenerTablePreviewProps) {
  const { data, chainsById, marketSectorsById, isLoading } = useScreenerTableData(15)

  return (
    <div className={className}>
      {/* Inner card wrapper */}
      <div className="border-t border-l border-solid border-border-default rounded-tl-xl overflow-hidden">
        <div className="max-h-[480px] overflow-hidden relative" style={FADE_MASK_STYLE}>
          {isLoading ? (
            <div className="p-4">
              <TableSkeleton />
            </div>
          ) : (
            <div className="overflow-x-auto relative z-0">
              <table className="w-full border-collapse table-fixed">
                <thead>
                  <tr className="border-b border-border-default">
                    <HeaderCell label="Project" align="left" width="160px" />
                    <HeaderCell label="Market sector" align="left" width="140px" />
                    <HeaderCell label="Chains" align="left" width="80px" />
                    <HeaderCell label="Fees" subLabel="7d" width="90px" />
                    <HeaderCell label="Fees" subLabel="30d" width="90px" />
                    <HeaderCell label="Fees" subLabel="180d" width="90px" />
                    <HeaderCell label="Fees" subLabel="Trend 7d" width="90px" />
                    <HeaderCell label="Fees" subLabel="Trend 30d" width="100px" />
                    <HeaderCell label="Fees" subLabel="Trend 180d" width="100px" />
                    <HeaderCell label="Revenue" subLabel="7d" width="90px" />
                    <HeaderCell label="Revenue" subLabel="30d" width="90px" />
                    <HeaderCell label="Revenue" subLabel="180d" width="100px" />
                    <HeaderCell label="Revenue" subLabel="Trend 7d" width="100px" />
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, index) => {
                    const fees7dTrend = formatTrend(row.fees_7d_trend)
                    const fees30dTrend = formatTrend(row.fees_30d_trend)
                    const fees180dTrend = formatTrend(row.fees_180d_trend)
                    const revenue7dTrend = formatTrend(row.revenue_7d_trend)

                    return (
                      <tr
                        key={row.slug || index}
                        className="border-b border-border-default last:border-b-0 hover:bg-bg-muted/30 transition-colors"
                      >
                        {/* Project */}
                        <td className="py-2 px-3" style={{ width: "160px" }}>
                          <div className="flex items-center gap-2">
                            <span className="w-4 text-xs text-fg-tertiary tabular-nums text-right shrink-0">
                              {index + 1}
                            </span>
                            <ProjectLogo logo={row.logo} name={row.name} size={18} />
                            <span className="text-sm font-medium text-fg-default truncate">
                              {row.name}
                            </span>
                            {row.symbol && (
                              <span className="text-[10px] text-fg-tertiary font-mono uppercase shrink-0">
                                {row.symbol}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Market Sector */}
                        <td className="py-2 px-3" style={{ width: "140px" }}>
                          <span className="text-sm text-fg-secondary truncate block">
                            {row.market_sectors?.[0]
                              ? marketSectorsById.get(row.market_sectors[0])?.name ||
                                row.market_sectors[0]
                              : "-"}
                          </span>
                        </td>

                        {/* Chains */}
                        <td className="py-2 px-3" style={{ width: "80px" }}>
                          <ChainLogos
                            chainIds={row.chains || []}
                            chainsById={chainsById}
                            maxDisplay={3}
                          />
                        </td>

                        {/* Fees 7d */}
                        <td className="py-2 px-3 text-right" style={{ width: "90px" }}>
                          <span
                            className="text-sm tabular-nums text-fg-default whitespace-nowrap"
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {formatCurrency(row.fees_7d_sum)}
                          </span>
                        </td>

                        {/* Fees 30d */}
                        <td className="py-2 px-3 text-right" style={{ width: "90px" }}>
                          <span
                            className="text-sm tabular-nums text-fg-default whitespace-nowrap"
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {formatCurrency(row.fees_30d_sum)}
                          </span>
                        </td>

                        {/* Fees 180d */}
                        <td className="py-2 px-3 text-right" style={{ width: "90px" }}>
                          <span
                            className="text-sm tabular-nums text-fg-default whitespace-nowrap"
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {formatCurrency(row.fees_180d_sum)}
                          </span>
                        </td>

                        {/* Fees Trend 7d */}
                        <td className="py-2 px-3 text-right" style={{ width: "90px" }}>
                          <span
                            className={`text-sm tabular-nums whitespace-nowrap ${fees7dTrend.color}`}
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {fees7dTrend.text}
                          </span>
                        </td>

                        {/* Fees Trend 30d */}
                        <td className="py-2 px-3 text-right" style={{ width: "100px" }}>
                          <span
                            className={`text-sm tabular-nums whitespace-nowrap ${fees30dTrend.color}`}
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {fees30dTrend.text}
                          </span>
                        </td>

                        {/* Fees Trend 180d */}
                        <td className="py-2 px-3 text-right" style={{ width: "100px" }}>
                          <span
                            className={`text-sm tabular-nums whitespace-nowrap ${fees180dTrend.color}`}
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {fees180dTrend.text}
                          </span>
                        </td>

                        {/* Revenue 7d */}
                        <td className="py-2 px-3 text-right" style={{ width: "90px" }}>
                          <span
                            className="text-sm tabular-nums text-fg-default whitespace-nowrap"
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {formatCurrency(row.revenue_7d_sum)}
                          </span>
                        </td>

                        {/* Revenue 30d */}
                        <td className="py-2 px-3 text-right" style={{ width: "90px" }}>
                          <span
                            className="text-sm tabular-nums text-fg-default whitespace-nowrap"
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {formatCurrency(row.revenue_30d_sum)}
                          </span>
                        </td>

                        {/* Revenue 180d */}
                        <td className="py-2 px-3 text-right" style={{ width: "100px" }}>
                          <span
                            className="text-sm tabular-nums text-fg-default whitespace-nowrap"
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {formatCurrency(row.revenue_180d_sum)}
                          </span>
                        </td>

                        {/* Revenue Trend 7d */}
                        <td className="py-2 px-3 text-right" style={{ width: "100px" }}>
                          <span
                            className={`text-sm tabular-nums whitespace-nowrap ${revenue7dTrend.color}`}
                            style={{ fontFamily: "var(--font-geist-mono)" }}
                          >
                            {revenue7dTrend.text}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
